import { useEffect, useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwApi, fmtDate } from "./hwApi";

const STATUS_LABEL = { correct: "Correct", partially_correct: "Partially correct", incorrect: "Incorrect",
                       not_attempted: "Not attempted", needs_clarification: "Needs clarification", unmarked: "—" };

function ProgressInner() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    hwApi.get("/analytics/me").then(({ data: d }) => setData(d)).catch(() => setError("Could not load analytics."));
    hwApi.get("/report").then(({ data: d }) => setReport(d)).catch(() => {});
  }, []);

  if (error) return <div className="tp-wrap"><p className="tp-error">{error}</p></div>;
  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;

  const maxPct = Math.max(1, ...data.marks_over_time.map((m) => m.pct));

  return (
    <div className="tp-wrap" style={{ maxWidth: 820 }} data-testid="hw-progress">
      <div className="tp-eyebrow">MY PROGRESS · LAST {data.period_days} DAYS</div>
      <h1 className="tp-h2" style={{ fontSize: 30 }}>Personal improvement dashboard</h1>
      <p className="tp-muted">Every number here comes from Pranjal Sir's own evaluation of your handwritten work — never from AI.</p>

      <div className="tp-grid-4" style={{ marginTop: 16 }} data-testid="hw-progress-stats">
        {[[data.assigned, "Assigned"], [data.submitted, "Submitted"], [data.evaluated, "Checked"], [`${data.consistency}%`, "Consistency"]].map(([v, l]) => (
          <div className="tp-card" key={l} style={{ textAlign: "center" }}>
            <b style={{ fontSize: 22 }}>{v}</b>
            <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>{l}</p>
          </div>
        ))}
      </div>

      <div className="tp-grid-4" style={{ marginTop: 12 }} data-testid="hw-daily-stats">
        <div className="tp-card" style={{ textAlign: "center", border: "1px solid #1d4ed8" }} data-testid="hw-streak-card">
          <b style={{ fontSize: 22, color: "#1d4ed8" }}>{data.current_streak} day{data.current_streak === 1 ? "" : "s"}</b>
          <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>Daily streak (on-time)</p>
        </div>
        <div className="tp-card" style={{ textAlign: "center", border: data.missed_days ? "1px solid #dc2626" : "1px solid #e4eaf2" }} data-testid="hw-missed-card">
          <b style={{ fontSize: 22, color: data.missed_days ? "#b42318" : "#15803d" }}>{data.missed_days}</b>
          <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>Missed days</p>
        </div>
        <div className="tp-card" style={{ textAlign: "center" }}>
          <b style={{ fontSize: 22 }}>{data.on_time}/{data.daily_total}</b>
          <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>Daily homework on time</p>
        </div>
        <div className="tp-card" style={{ textAlign: "center" }}>
          <b style={{ fontSize: 22 }}>{data.percentage}%</b>
          <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>Overall marks</p>
        </div>
      </div>

      {data.missed_days > 0 && (
        <div className="tp-panel" style={{ marginTop: 12, border: "1px solid #dc2626", background: "#fef2f2" }} data-testid="hw-missed-warning">
          <b style={{ color: "#b42318" }}>You missed {data.missed_days} daily homework day{data.missed_days === 1 ? "" : "s"}</b>
          <p className="tp-muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
            Missed days pull down your consistency and are reported to Pranjal Sir in your {data.period_days}-day report.
            Submit today's homework before 11:59 PM to protect your streak.</p>
        </div>
      )}

      <div className="tp-panel" style={{ marginTop: 16 }}>
        <div className="admin-row" style={{ justifyContent: "space-between" }}>
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Marks over time</h2>
          {data.improvement_points != null && (
            <span className="tp-note" style={{ color: data.improvement_points >= 0 ? "#15803d" : "#b42318" }} data-testid="hw-trend">
              {data.improvement_points >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {data.improvement_points >= 0 ? "+" : ""}{data.improvement_points} pts vs earlier
            </span>
          )}
        </div>
        {data.marks_over_time.length === 0 && <p className="tp-muted" data-testid="hw-progress-empty">No evaluated homework in this period yet.</p>}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 110, marginTop: 12 }} data-testid="hw-marks-bars">
          {data.marks_over_time.map((m) => (
            <div key={m.assignment_id} title={`${m.title}: ${m.pct}%`}
                 style={{ flex: 1, maxWidth: 40, height: `${(m.pct / maxPct) * 100}%`, minHeight: 4, borderRadius: "6px 6px 0 0",
                          background: m.pct >= 70 ? "#16a34a" : m.pct >= 40 ? "#f59e0b" : "#dc2626" }} />
          ))}
        </div>
      </div>

      <div className="tp-panel" style={{ marginTop: 16 }} data-testid="hw-error-freq">
        <h2 className="tp-h2" style={{ fontSize: 18 }}>Most frequent errors</h2>
        {data.error_frequency.length === 0 && <p className="tp-muted">No error labels yet — they appear after your first checked homework.</p>}
        {data.error_frequency.map((e) => (
          <div className="admin-row" key={e.category} style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hw-error-${e.category.replace(/\W+/g, "-")}`}>
            <span style={{ flex: 1, color: "#1e3a5f" }}>{e.category}</span>
            <b style={{ color: "#b42318" }}>{e.count}×</b>
          </div>
        ))}
      </div>

      <div className="tp-panel" style={{ marginTop: 16 }} data-testid="hw-topic-perf">
        <h2 className="tp-h2" style={{ fontSize: 18 }}>Topic performance</h2>
        {data.topic_performance.length === 0 && <p className="tp-muted">Nothing evaluated yet.</p>}
        {data.topic_performance.map((t) => (
          <div key={t.topic} style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hw-topicperf-${t.topic.replace(/\W+/g, "-")}`}>
            <div className="admin-row">
              <b style={{ flex: 1, color: "#0b2545", fontSize: 14 }}>{t.topic}</b>
              <b style={{ color: "#1d4ed8" }}>{t.awarded}/{t.max}</b>
            </div>
            <p className="tp-note" style={{ margin: "4px 0 0" }}>
              {t.attempted} questions · ✓ {t.correct} · ~ {t.partially_correct} · ✗ {t.incorrect}{t.not_attempted ? ` · skipped ${t.not_attempted}` : ""}
            </p>
          </div>
        ))}
      </div>

      {report && (
        <div className="tp-panel" style={{ marginTop: 16, border: "1px solid #1d4ed8" }} data-testid="hw-report-card">
          <div className="tp-eyebrow">{report.period_days}-DAY REPORT</div>
          <div className="admin-row" style={{ marginTop: 6, flexWrap: "wrap", gap: 14 }}>
            <span>Average <b>{report.avg_percentage}%</b></span>
            <span>Best <b style={{ color: "#15803d" }}>{report.highest}%</b></span>
            <span>Lowest <b style={{ color: "#b42318" }}>{report.lowest}%</b></span>
            <span>Checked <b>{report.evaluated}</b>/{report.assigned}</span>
          </div>
          {report.teacher_comment ? (
            <div style={{ marginTop: 12, background: "#eff6ff", borderRadius: 10, padding: 14 }} data-testid="hw-teacher-comment">
              <b style={{ color: "#1d4ed8", fontSize: 13 }}>PRANJAL SIR'S NOTE{report.teacher_comment_at ? ` · ${fmtDate(report.teacher_comment_at)}` : ""}</b>
              <p style={{ margin: "6px 0 0", color: "#0b2545" }}>{report.teacher_comment}</p>
            </div>
          ) : (
            <p className="tp-muted" style={{ marginTop: 10 }}>Pranjal Sir's summary note will appear here.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function HomeworkProgress() {
  return <HomeworkShell><ProgressInner /></HomeworkShell>;
}
